// Game menu overlay component

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Game menu overlay component
 * Provides options to exit the game
 */
class GameMenu extends BaseComponent {
  static properties = {
    open: { type: Boolean },
  };

  static styles = css`
    :host {
      display: block;
    }

    .game-menu-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2000;
      backdrop-filter: blur(4px);
    }

    .game-menu-panel {
      background: rgba(0, 0, 0, 0.95);
      border: 1px solid #444;
      border-radius: 12px;
      padding: 24px;
      min-width: 300px;
      max-width: 400px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    }

    .game-menu-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 1px solid #444;
    }

    .game-menu-header h3 {
      color: #fff;
      margin: 0;
      font-size: 20px;
      font-weight: 600;
    }

    .game-menu-close {
      background: transparent;
      border: none;
      color: #fff;
      cursor: pointer;
      padding: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background 0.2s;
    }

    .game-menu-close:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .game-menu-items {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .game-menu-item {
      display: flex;
      align-items: center;
      gap: 12px;
      background: #333;
      border: 1px solid #555;
      color: #fff;
      padding: 12px 16px;
      border-radius: 8px;
      cursor: pointer;
      font-size: 16px;
      transition: all 0.2s;
      width: 100%;
      text-align: left;
    }

    .game-menu-item:hover {
      background: #444;
      border-color: #666;
    }

    .game-menu-item:active {
      background: #222;
      transform: translateY(1px);
    }

    .icon {
      width: 20px;
      height: 20px;
    }
  `;

  constructor() {
    super();
    this.open = false;
  }

  _handleClose() {
    this.dispatchEvent(new CustomEvent('close', { bubbles: true }));
  }

  _handleExitGame() {
    this.dispatchEvent(new CustomEvent('exit-game', { bubbles: true }));
    this._handleClose();
  }

  _handleRestart() {
    this.dispatchEvent(new CustomEvent('restart-adventure', { bubbles: true }));
    this._handleClose();
  }

  /** @param {Event} e */
  _handleOverlayClick(e) {
    if (e.target === e.currentTarget) {
      this._handleClose();
    }
  }

  _renderXIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
      </svg>
    `;
  }

  _renderHomeIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    `;
  }

  _renderRestartIcon() {
    // Lucide RotateCcw — counterclockwise arrow + dot.
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
        <path d="M3 3v5h5" />
      </svg>
    `;
  }

  render() {
    if (!this.open) {
      return html``;
    }

    return html`
      <div class="game-menu-overlay" @click="${this._handleOverlayClick}">
        <div class="game-menu-panel">
          <div class="game-menu-header">
            <h3>Game Menu</h3>
            <button class="game-menu-close" @click="${this._handleClose}">${this._renderXIcon()}</button>
          </div>
          <div class="game-menu-items">
            <button class="game-menu-item" @click="${this._handleExitGame}">
              ${this._renderHomeIcon()}
              <span>Exit to Main Menu</span>
            </button>
            <button class="game-menu-item" @click="${this._handleRestart}">
              ${this._renderRestartIcon()}
              <span>Restart this adventure</span>
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('game-menu', GameMenu);
